import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  TextField,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Email,
  CalendarMonth,
  Schedule,
  Send,
  ExpandMore,
} from '@mui/icons-material';
import PublicLayout from '../components/PublicLayout';
import { ApiStatus } from '../components/marketing';
import { marketingLinks } from '../constants/marketingEvent';
import { useTranslation } from 'react-i18next';
import {
  marketingAccentTileSx,
  marketingBandSx,
  marketingColor,
} from '../theme/marketingTokens';
import { makeApiCall } from '../utils/apiConfig';

export const ContactPage: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleInputChange = (field: string) => (event: any) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      const response = await makeApiCall('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'contact',
          name: formData.name,
          email: formData.email,
          company: formData.company || undefined,
          subject: formData.subject,
          message: formData.message,
        }),
      });
      if (!response.ok) {
        throw new Error(`Contact submission failed with status ${response.status}`);
      }
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const faqs = t('public.contact.faq.items', { returnObjects: true }) as {
    question: string;
    answer: string;
  }[];
  const contactInfo = t('public.contact.info', { returnObjects: true }) as {
    id: string;
    title: string;
    details: string;
    description: string;
  }[];
  // Keyed by id rather than array position, so removing or reordering an
  // entry can't silently shift every icon after it.
  const contactIcons: Record<string, typeof Email> = {
    email: Email,
    call: CalendarMonth,
    hours: Schedule,
  };

  return (
    <PublicLayout>
      <Box>
      {/* Header */}
      <Box
        sx={{
          ...marketingBandSx,
          borderTop: 'none',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
            {t('public.contact.header.title')}
          </Typography>
          <Typography variant="h6" sx={{ color: marketingColor.textSecondary, maxWidth: '600px', mx: 'auto' }}>
            {t('public.contact.header.subtitle')}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Contact Form */}
          <Box sx={{ flex: 2 }}>
            <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
              {t('public.contact.form.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {t('public.contact.form.subtitle')}
            </Typography>

            <Paper sx={{ p: 4 }}>
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  {submitStatus && (
                    <Alert severity={submitStatus} onClose={() => setSubmitStatus(null)}>
                      {t(`public.contact.form.${submitStatus}`)}
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      fullWidth
                      label={t('public.contact.form.fields.name')}
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      required
                    />
                    <TextField
                      fullWidth
                      label={t('public.contact.form.fields.email')}
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      required
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label={t('public.contact.form.fields.company')}
                    value={formData.company}
                    onChange={handleInputChange('company')}
                  />
                  <TextField
                    fullWidth
                    label={t('public.contact.form.fields.subject')}
                    value={formData.subject}
                    onChange={handleInputChange('subject')}
                    required
                  />
                  <TextField
                    fullWidth
                    label={t('public.contact.form.fields.message')}
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange('message')}
                    required
                    placeholder={t('public.contact.form.fields.messagePlaceholder')}
                  />
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<Send />}
                      disabled={submitting}
                      sx={{ py: 2, px: 4 }}
                    >
                      {t('public.contact.form.submit')}
                    </Button>
                  </Box>
                </Stack>
              </form>
            </Paper>
          </Box>

          {/* Contact Information */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
              {t('public.contact.infoTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {t('public.contact.infoSubtitle')}
            </Typography>

            <Stack spacing={3}>
              {contactInfo.map((info) => (
                <Card key={info.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          ...marketingAccentTileSx,
                          mr: 2,
                        }}
                      >
                      {React.createElement(contactIcons[info.id] ?? Email)}
                      </Box>
                      <Typography variant="h6" fontWeight="bold">
                        {info.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                      {info.id === 'call' ? (
                        <Box
                          component="a"
                          href={marketingLinks.scheduler}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: marketingColor.accent,
                            textDecoration: 'underline',
                            textUnderlineOffset: '3px',
                          }}
                        >
                          {info.details}
                        </Box>
                      ) : (
                        info.details
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {info.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        </Box>

        {/* Carried over from the retired support page: the questions that are
            not already answered on /pricing, and the live API health check. */}
        <Box sx={{ mt: 10, mb: 8 }}>
          <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" textAlign="center">
            {t('public.contact.faq.title')}
          </Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
            {t('public.contact.faq.subtitle')}
          </Typography>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" fontWeight="bold">
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        <ApiStatus />
      </Container>
      </Box>
    </PublicLayout>
  );
};

export default ContactPage;
